import { ThemeEvents } from '@theme/events';

/**
 * Keeps the Tabby "pay later" promo in sync with the selected variant.
 *
 * Tabby mounts the promo with `ReactDOM.createRoot`, caching one root per container element in
 * a WeakMap keyed off the node. Re-running `new TabbyPromo()` against the same node therefore
 * re-renders the existing root, which is exactly what a price change wants: React updates the
 * instalment amount in place, with no unmount and no visible gap.
 *
 * The Tabby app block breaks that. Its own refresh does `container.innerHTML = ''` before
 * calling `new TabbyPromo()` again, which rips React's DOM out from under a root that is still
 * cached. The re-render then diffs against nodes that are no longer in the document and paints
 * nothing, so the promo disappears. That refresh runs on every variant change, because the app
 * block observes `input[name="id"]` for `value` attribute mutations and the theme's morph
 * rewrites that attribute while updating the product form.
 *
 * So this module does two things:
 *
 * 1. Guards the container against being cleared, so the app block's re-render lands on an
 *    intact root and updates in place instead of blanking the promo.
 * 2. Drives its own refresh from the theme's `variant:update` event, rather than relying on the
 *    app block's observer firing as a side effect of an unrelated attribute rewrite.
 *
 * Both paths render into the same node, so they are idempotent with respect to each other.
 */

const CONTAINER_ID = 'TabbyPromo';

/** Coalesces the bursts of variant updates that rapid option clicking produces. */
const REFRESH_DELAY = 150;

/** Tabby mounts on a `setTimeout(…, 50)`, so verify the render well after it should have landed. */
const VERIFY_DELAY = 500;

const SCRIPT_READY_TIMEOUT = 10000;
const SCRIPT_POLL_INTERVAL = 100;

/**
 * @typedef {object} TabbyConfig
 * @property {string} publicKey - The merchant's Tabby public key.
 * @property {string} currency - ISO currency code.
 * @property {string} lang - Two letter language code.
 */

/**
 * Yields the source of every inline app block script that sets up a Tabby promo.
 * @returns {Generator<string>} The script sources.
 */
function* appBlockSources() {
  for (const script of document.querySelectorAll('.shopify-app-block script:not([src])')) {
    const source = script.textContent ?? '';
    if (source.includes('TabbyPromo')) yield source;
  }
}

/** @type {TabbyConfig | null | undefined} */
let cachedConfig;

/**
 * Reads the Tabby configuration out of the app block's own inline script, so the merchant's
 * public key stays owned by the app and is never duplicated into the theme.
 * @returns {TabbyConfig | null} The configuration, or null if the app block is not on the page.
 */
function readConfig() {
  if (cachedConfig !== undefined) return cachedConfig;

  for (const source of appBlockSources()) {
    const publicKey = source.match(/publicKey\s*=\s*'([^']+)'/)?.[1];
    if (!publicKey) continue;

    cachedConfig = {
      publicKey,
      currency: source.match(/currency\s*=\s*'([^']+)'/)?.[1] ?? 'AED',
      lang: source.match(/lang\s*=\s*'([^']+)'/)?.[1] ?? 'en',
    };
    return cachedConfig;
  }

  cachedConfig = null;
  return cachedConfig;
}

/**
 * Tabby exposes a different merchant code per market.
 * @param {string} currency - ISO currency code.
 * @returns {string | undefined} The merchant code, or undefined when the currency is unsupported.
 */
function merchantCodeFor(currency) {
  switch (currency) {
    case 'AED':
      return 'default';
    case 'SAR':
      return 'ksa';
    case 'KWD':
      return 'KW';
    default:
      return undefined;
  }
}

/**
 * Resolves once Tabby's script has defined the global constructor.
 * @returns {Promise<boolean>} Whether the constructor became available before the timeout.
 */
function whenTabbyReady() {
  if (typeof window.TabbyPromo === 'function') return Promise.resolve(true);

  return new Promise((resolve) => {
    const deadline = Date.now() + SCRIPT_READY_TIMEOUT;
    const poll = setInterval(() => {
      if (typeof window.TabbyPromo === 'function') {
        clearInterval(poll);
        resolve(true);
      } else if (Date.now() > deadline) {
        clearInterval(poll);
        resolve(false);
      }
    }, SCRIPT_POLL_INTERVAL);
  });
}

/** @type {WeakSet<Element>} */
const guarded = new WeakSet();

/**
 * Makes clearing the container a no-op.
 *
 * The app block empties the container before every re-render. React's root stays cached against
 * the node either way, so the clear only serves to detach the DOM that re-render is about to
 * diff against — dropping it is what turns the re-render back into a working in-place update.
 * Assignments of real markup still go through, and React never takes this path itself: it builds
 * the promo with `appendChild` and clears roots with `textContent`.
 * @param {Element} container - The promo container.
 * @returns {Element} The same container, for chaining.
 */
function guardAgainstClearing(container) {
  if (guarded.has(container)) return container;

  const { get, set } = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML') ?? {};
  if (!get || !set) return container;

  Object.defineProperty(container, 'innerHTML', {
    configurable: true,
    get() {
      return get.call(this);
    },
    /** @param {string} value */
    set(value) {
      if (value === '') return;
      set.call(this, value);
    },
  });

  guarded.add(container);
  return container;
}

/**
 * Swaps the promo container for an empty copy of itself.
 *
 * Only used to recover from a promo that rendered nothing: Tabby keys its cached React root off
 * the container node, so a new node is the one way to force a fresh root.
 * @param {HTMLElement} current - The container to replace.
 * @returns {HTMLElement} The replacement container.
 */
function resetContainer(current) {
  const replacement = /** @type {HTMLElement} */ (document.createElement(current.tagName));
  for (const { name, value } of current.attributes) {
    replacement.setAttribute(name, value);
  }

  current.replaceWith(replacement);
  return replacement;
}

/**
 * Builds the promo options for a price.
 * @param {TabbyConfig} config - The app block's configuration.
 * @param {number} priceInCents - The variant price, in cents.
 * @returns {Record<string, unknown>} The options to hand to `TabbyPromo`.
 */
function optionsFor(config, priceInCents) {
  /** @type {Record<string, unknown>} */
  const options = {
    selector: `#${CONTAINER_ID}`,
    currency: config.currency,
    price: priceInCents / 100,
    lang: config.lang,
    publicKey: config.publicKey,
  };

  const merchantCode = merchantCodeFor(config.currency);
  if (merchantCode) options.merchantCode = merchantCode;

  return options;
}

/**
 * Re-renders the promo for a given price, in place wherever possible.
 * @param {number} priceInCents - The selected variant's price, in cents.
 */
async function refresh(priceInCents) {
  const config = readConfig();
  if (!config || !(priceInCents > 0)) return;
  if (!document.getElementById(CONTAINER_ID)) return;
  if (!(await whenTabbyReady())) return;

  let container = /** @type {HTMLElement | null} */ (document.getElementById(CONTAINER_ID));
  if (!container) return;

  guardAgainstClearing(container);

  // An empty container means the last render left a root pointing at detached DOM, which no
  // amount of re-rendering will bring back. Start over on a fresh node instead.
  if (container.childElementCount === 0) guardAgainstClearing((container = resetContainer(container)));

  const options = optionsFor(config, priceInCents);
  new window.TabbyPromo(options);

  // The promo can still come up empty — if something cleared the container before the guard was
  // installed, for instance. Rebuild once if it did.
  setTimeout(() => {
    const rendered = /** @type {HTMLElement | null} */ (document.getElementById(CONTAINER_ID));
    if (!rendered || rendered.childElementCount > 0) return;

    guardAgainstClearing(resetContainer(rendered));
    new window.TabbyPromo(options);
  }, VERIFY_DELAY);
}

/**
 * The price the app block rendered on page load.
 *
 * Read eagerly at module load: `input[name="id"]` names the selected variant only until the
 * first variant change, after which the theme has already pointed it at the new one.
 * @returns {number | undefined} The price in cents, or undefined if it cannot be determined.
 */
function initialPrice() {
  const input = document.querySelector('input[name="id"]');
  if (!(input instanceof HTMLInputElement)) return undefined;

  for (const source of appBlockSources()) {
    const match = source.match(/const productData = (\{[\s\S]*?\});?\s*\n/);
    if (!match) continue;
    try {
      const variant = JSON.parse(match[1]).variants?.find((v) => String(v.id) === input.value);
      if (variant) return variant.price;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** @type {ReturnType<typeof setTimeout> | undefined} */
let pending;

/**
 * The price the promo is currently showing. Tabby only displays an instalment amount, so a
 * variant change that leaves the price alone — picking a frame colour, say — needs no render.
 * @type {number | undefined}
 */
let renderedPrice = initialPrice();

// Guard the container up front, so the app block's own refresh cannot blank the promo even on
// the variant changes this module deliberately sits out.
const initialContainer = document.getElementById(CONTAINER_ID);
if (initialContainer) guardAgainstClearing(initialContainer);

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const price = /** @type {{ detail?: { resource?: { price?: number } } }} */ (event).detail?.resource?.price;
  if (typeof price !== 'number') return;
  if (price === renderedPrice) return;

  clearTimeout(pending);
  pending = setTimeout(() => {
    renderedPrice = price;
    refresh(price);
  }, REFRESH_DELAY);
});
